import { AssetsProvider, Image, styled, Text, useTranslation } from '@obvio/app'
import { EventCarousel, PartnersCarousel, RichText } from '@obvio/template'
import { Grid, GridItem, Stack } from '@obvio/ui'
import { descSort } from '@obvio/utils'
import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'

import { EventArtGrid } from './EventArtGrid'
import { EventArtModal } from './EventArtModal'
import { EventHeading } from './EventHeading'
import { EventRegisterInline } from './EventRegisterInline'
import { EventRegisterModal } from './EventRegisterModal'
import { EventResponse } from './EventResponse'
import { CenteredSection } from '../CenteredSection'

import type { EventPageProps, FormStatus } from './EventPageVip'
import type { ImageAsset } from '@obvio/app'
import type { ReactElement } from 'react'

const Hero = styled(Grid)`
  margin-bottom: ${(theme) => theme.spacing.extraLarge};
  text-transform: uppercase;
  width: 100%;
  min-height: 27rem;
`

const HeroContent = styled(GridItem)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
`

const IntroText = styled(Text)`
  margin-bottom: ${(theme) => theme.spacing.medium};
  text-transform: uppercase;
`

const Info = styled(Grid)`
  width: 100%;
`
const InfoContent = styled(HeroContent)`
  justify-content: flex-start;
  text-align: left;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: black;
`

const IndexInfoWrap = styled(Stack)`
  width: 100%;
  text-align: left;
`

export function EventPage({
  artworks,
  date,
  endDate,
  endTime,
  id,
  images,
  invites,
  startTime,
  title,
  description,
  place,
  placeUrl,
  rodoRequired,
  hasPlaceLeft,
  eventPartners,
}: Omit<EventPageProps, 'isVip'>): ReactElement {
  const { t } = useTranslation()
  const [status, setStatus] = useState<FormStatus | null>(null)
  const [selectedArt, setSelectedArt] = useState<string | null>(null)

  const resetStatus = () => setStatus(null)
  const changeStatus = (newStatus: FormStatus) => () => setStatus(newStatus)
  const resetArt = () => setSelectedArt(null)

  const image = images[0] as ImageAsset

  const sortedArtworks = artworks.sort((a, b) =>
    descSort(a.createdAt, b.createdAt),
  )

  const isActive = endDate
    ? Date.now() < new Date(endDate).getTime()
    : Date.now() < new Date(date).getTime()

  return (
    <AssetsProvider path={`/event/${id}`}>
      <CenteredSection>
        <Hero templateColumns="1fr 1fr" templateRows="1fr" gap="medium">
          <HeroContent noCenter>
            <IntroText tag="span">{t('label.exhibition')}</IntroText>
            <EventHeading
              title={title}
              date={date.toDateString()}
              startTime={startTime ?? undefined}
              endTime={endTime ?? undefined}
              place={place ?? undefined}
              placeUrl={placeUrl ?? undefined}
              small
            />
          </HeroContent>
          <Image img={image} />
        </Hero>
      </CenteredSection>
      <CenteredSection>
        <Info templateColumns="2fr 1fr" gap="extraLarge">
          <InfoContent>
            <IntroText tag="span">{t('label.about')}</IntroText>
            <RichText value={description} />
          </InfoContent>
          {invites ? (
            <EventRegisterInline
              place={place}
              placeUrl={placeUrl}
              time={date.toLocaleDateString()}
              handleRegister={changeStatus('form')}
            />
          ) : null}
        </Info>
      </CenteredSection>
      <CenteredSection>
        <PartnersCarousel
          partners={eventPartners.map((v) => ({ ...v, image: v.image[0] }))}
        />
      </CenteredSection>
      <CenteredSection>
        <IndexInfoWrap kind="vertical">
          <Divider />
          <Text tag="span" as="sub-h2">
            {t('event.artworkIndex')}
          </Text>
        </IndexInfoWrap>
      </CenteredSection>
      <EventArtGrid artworks={sortedArtworks} selectArt={setSelectedArt} />
      {invites && isActive ? (
        <AnimatePresence>
          <EventRegisterModal
            id={id}
            open={status === 'form'}
            close={resetStatus}
            title={title}
            place={place}
            placeUrl={placeUrl}
            date={date.toLocaleDateString()}
            accept={changeStatus('accept')}
            rodoRequired={rodoRequired}
            hasPlaceLeft={hasPlaceLeft}
            onNewClick={() => setStatus('form')}
          />
          {(status === 'decline' || status === 'accept') && (
            <EventResponse
              close={resetStatus}
              decline={status === 'decline'}
              eventId={id}
              onNewClick={() => setStatus('form')}
            />
          )}
        </AnimatePresence>
      ) : (
        (null as unknown as ReactElement)
      )}
      <CenteredSection>
        <EventCarousel />
      </CenteredSection>
      <EventArtModal
        open={Boolean(selectedArt)}
        close={resetArt}
        selected={artworks.find((art) => selectedArt === art.id)}
      />
    </AssetsProvider>
  )
}
